import {
  prepareLenderDebitMulticall,
  prepareDebitDataMulticall,
} from '@1delta/calldata-sdk'
import { getEvmChain } from '@1delta/providers'
import { createMulticallRpcCall } from '../../../utils/rpcCall'
import { prepareMulticallInputs, type Call } from '../../../utils/multicall'
import { MULTICALL_DEFAULT_BATCH_SIZE } from '../../../utils/constants'
import {
  LenderUserQuery,
  organizeUserQueries,
} from '../../../types/lenderTypes'
import { getAbi } from '../abis'
import { buildUserCall } from '../fetch-balances/prepare'
import type {
  PermissionParams,
  TokenApprovalParams,
  PreparedMergedRpcCalls,
  PreparedMergedMulticallParams,
  TokenApprovalMeta,
  ExtraValidationCall,
} from './types'

const EMPTY_TOKEN_APPROVAL_META: TokenApprovalMeta = {
  tokenAddresses: [],
  spenders: [],
}

/**
 * Prepares a single set of multicall RPC calls that fetches
 * token approval data, permission/delegation data, AND user balance data
 * in one round-trip.
 */
export async function prepareMergedRpcCalls(
  chainId: string,
  balanceQueries: LenderUserQuery[],
  permissionParams: PermissionParams,
  tokenApprovalParams?: TokenApprovalParams,
  batchSize = MULTICALL_DEFAULT_BATCH_SIZE,
  blockTag = 'latest',
  allowFailure = true,
  extraCalls: ExtraValidationCall[] = [],
): Promise<PreparedMergedRpcCalls> {
  const multicallAddress = getEvmChain(chainId).contracts?.multicall3?.address!

  // 1. Token approval calls → PreparedCall[]
  let tokenApprovalPreparedCalls: ReturnType<typeof prepareMulticallInputs> = []
  let tokenApprovalMeta: TokenApprovalMeta = EMPTY_TOKEN_APPROVAL_META
  if (tokenApprovalParams) {
    const taResult = prepareDebitDataMulticall(tokenApprovalParams)
    tokenApprovalPreparedCalls = prepareMulticallInputs(
      taResult.abi,
      taResult.calls as Call[],
    )
    tokenApprovalMeta = {
      tokenAddresses: taResult.meta.tokenAddresses,
      spenders: taResult.meta.spenders,
      tokenListForChain: tokenApprovalParams.tokenListForChain,
    }
  }

  // 2. Permission calls → PreparedCall[]
  const permissionResult = prepareLenderDebitMulticall(permissionParams)
  const permissionPreparedCalls = prepareMulticallInputs(
    permissionResult.abi,
    permissionResult.calls as Call[],
  )

  // 3. Extra validation calls → PreparedCall[]
  const extraPreparedCalls = extraCalls.length
    ? prepareMulticallInputs(
        extraCalls.map((c) => c.abi),
        extraCalls.map((c) => c.call),
      )
    : []

  // 4. Balance calls → PreparedCall[]
  let balanceCalls: { call: Call; abi: any }[] = []
  const organizedQueries = organizeUserQueries(balanceQueries)
  for (const query of organizedQueries) {
    const abi = getAbi(query.lender)
    const callData = await buildUserCall(
      chainId,
      query.lender,
      query.account,
      query.params,
    )
    const mappedCalls = callData.map((call) => ({ call, abi }))
    balanceCalls = [...balanceCalls, ...mappedCalls]
  }
  const balancePreparedCalls = prepareMulticallInputs(
    balanceCalls.map((c) => c.abi),
    balanceCalls.map((c) => c.call),
  )

  // 5. Merge: [...tokenApproval, ...permission, ...extra, ...balance]
  const mergedCalls = [
    ...tokenApprovalPreparedCalls,
    ...permissionPreparedCalls,
    ...extraPreparedCalls,
    ...balancePreparedCalls,
  ]

  // 6. Create multicall RPC batches
  const batches = createMulticallRpcCall(
    mergedCalls,
    multicallAddress,
    batchSize,
    blockTag,
    allowFailure,
  )

  return {
    batches,
    preparedCalls: mergedCalls,
    rpcCalls: batches.map((b) => b.call),
    tokenApprovalCallCount: tokenApprovalPreparedCalls.length,
    permissionCallCount: permissionPreparedCalls.length,
    extraCallCount: extraPreparedCalls.length,
    tokenApprovalMeta,
    permissionMeta:
      permissionResult.meta as PreparedMergedRpcCalls['permissionMeta'],
    balanceQueries,
  }
}

/**
 * Prepares merged token approval + permission + balance calls in the format
 * expected by `multicallRetry`: flat calls[] and per-call abis[].
 */
export async function prepareMergedMulticallParams(
  chainId: string,
  balanceQueries: LenderUserQuery[],
  permissionParams: PermissionParams,
  tokenApprovalParams?: TokenApprovalParams,
  extraCalls: ExtraValidationCall[] = [],
): Promise<PreparedMergedMulticallParams> {
  // 1. Token approval calls
  let tokenApprovalCalls: Call[] = []
  let tokenApprovalAbis: any[] = []
  let tokenApprovalMeta: TokenApprovalMeta = EMPTY_TOKEN_APPROVAL_META
  if (tokenApprovalParams) {
    const taResult = prepareDebitDataMulticall(tokenApprovalParams)
    tokenApprovalCalls = taResult.calls.map((c) => ({
      address: c.address,
      name: c.name,
      params: c.params as any[],
    }))
    tokenApprovalAbis = tokenApprovalCalls.map(() => taResult.abi)
    tokenApprovalMeta = {
      tokenAddresses: taResult.meta.tokenAddresses,
      spenders: taResult.meta.spenders,
      tokenListForChain: tokenApprovalParams.tokenListForChain,
    }
  }

  // 2. Permission calls
  const permissionResult = prepareLenderDebitMulticall(permissionParams)
  const permissionCalls: Call[] = permissionResult.calls.map((c) => ({
    address: c.address,
    name: c.name,
    params: c.params as any[],
  }))
  const permissionAbis = permissionCalls.map(() => permissionResult.abi)

  // 3. Extra validation calls (caller-supplied)
  const extraCallsAbis = extraCalls.map((c) => c.abi)
  const extraCallsList = extraCalls.map((c) => c.call)

  // 4. Balance calls
  let balanceCalls: { call: Call; abi: any }[] = []
  const organizedQueries = organizeUserQueries(balanceQueries)
  for (const query of organizedQueries) {
    const abi = getAbi(query.lender)
    const callData = await buildUserCall(
      chainId,
      query.lender,
      query.account,
      query.params,
    )
    const mappedCalls = callData.map((call) => ({ call, abi }))
    balanceCalls = [...balanceCalls, ...mappedCalls]
  }

  // 5. Merge: [...tokenApproval, ...permission, ...extra, ...balance]
  return {
    calls: [
      ...tokenApprovalCalls,
      ...permissionCalls,
      ...extraCallsList,
      ...balanceCalls.map((c) => c.call),
    ],
    abis: [
      ...tokenApprovalAbis,
      ...permissionAbis,
      ...extraCallsAbis,
      ...balanceCalls.map((c) => c.abi),
    ],
    tokenApprovalCallCount: tokenApprovalCalls.length,
    permissionCallCount: permissionCalls.length,
    extraCallCount: extraCallsList.length,
    tokenApprovalMeta,
    permissionMeta:
      permissionResult.meta as PreparedMergedMulticallParams['permissionMeta'],
    balanceQueries,
  }
}
